import axios, { isCancel, type AxiosInstance } from "axios";
import { AuthContext } from "@/components/AuthProvider";
import { useCallback, useContext } from "react";
import { safeSessionStorage } from "@/lib/storage";
import { scopeOrgApiPath } from "@/lib/named-org";
import { instrumentApiClient } from "@/lib/interactionTrace";
import { instrumentApiClientForRecording } from "@/components/codesmith/sessionRecording/sessionRecorder";

declare module "axios" {
  interface AxiosRequestConfig {
    /** Skip page-instance scoping: the path already names its org's instance. */
    keepInstance?: boolean;
  }
}

const DEFAULT_TIMEOUT_MS = 60_000;

const clients = new Map<string, AxiosInstance>();

function getClient(
  timeoutMs: number,
  defaultInstanceSlug?: string | null,
): AxiosInstance {
  const key = JSON.stringify([timeoutMs, defaultInstanceSlug]);
  const existing = clients.get(key);
  if (existing) return existing;

  const client = axios.create({
    baseURL: `${process.env.NEXT_PUBLIC_API_URL}/api`,
    timeout: timeoutMs,
  });
  // First, because axios runs request interceptors in reverse: they see the org-scoped URL.
  instrumentApiClient(client);
  instrumentApiClientForRecording(client);

  client.interceptors.request.use(
    (config) => {
      config.withCredentials = true;
      if (config.url && !config.keepInstance)
        config.url = scopeOrgApiPath(config.url, defaultInstanceSlug);
      return config;
    },
    (error) => {
      console.error(`Error on request: ${error}`);
      return Promise.reject(error);
    },
  );

  client.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      if (isCancel(error)) return Promise.reject(error);
      console.error(`Error on response: ${error}`);
      if (error.response?.status === 401) {
        // TODO(aayush): Rationalize how we're going to show a re-login modal to the user
        // when their session expires due to inactivity.
        console.error("Unauthorized");
      }
      return Promise.reject(error);
    },
  );

  clients.set(key, client);
  return client;
}

/** The client `useApi` returns, for code outside React such as Storybook `beforeEach`. */
export function apiClient(
  timeout: number = DEFAULT_TIMEOUT_MS,
  defaultInstanceSlug?: string | null,
): AxiosInstance {
  return getClient(timeout, defaultInstanceSlug);
}

// useApi is a hook that returns an axios instance that is already hydrated with the
// proper base URL and authentication headers.
export const useApi = (timeout?: number) => {
  // Session storage can change between renders without a React state update.
  "use no memo";

  const { user } = useContext(AuthContext);
  const client = apiClient(timeout, user?.default_instance_slug);

  // Keep Authorization on defaults (not only in an interceptor) so raw-fetch
  // helpers that read `api.defaults.headers.common.Authorization` stay in sync
  // when `fa-test-token` is set after mount.
  if (typeof window !== "undefined") {
    // The opaque-origin canvas sandbox frame throws on storage access.
    const token = safeSessionStorage.get("fa-test-token");
    if (token) {
      client.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete client.defaults.headers.common.Authorization;
    }
  }

  return client;
};

// Helper function for streaming fetch requests that need proper authorization headers
export const useStreamingApi = () => {
  const { user } = useContext(AuthContext);
  const defaultInstanceSlug = user?.default_instance_slug;
  const api = useApi();

  const streamingFetch = useCallback(
    async (url: string, options: RequestInit = {}) => {
      const authorization = api.defaults.headers.common.Authorization;
      const headers = new Headers({
        Authorization: typeof authorization === "string" ? authorization : "",
        Accept: "application/x-ndjson",
      });
      new Headers(options.headers).forEach((value, name) =>
        headers.set(name, value),
      );

      return fetch(
        `${api.defaults.baseURL}/${scopeOrgApiPath(url, defaultInstanceSlug)}`,
        {
          ...options,
          headers,
          credentials: "include",
        },
      );
    },
    [
      api.defaults.baseURL,
      api.defaults.headers.common.Authorization,
      defaultInstanceSlug,
    ],
  );

  return { streamingFetch };
};
